import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import type { PaginatedResponse } from '@/types/tenant';
import type { LicenseListItem } from '@/types/license';

export const licenseKeys = {
  all: ['licenses'] as const,
  list: (page: number, pageSize: number, search: string) => [...licenseKeys.all, 'list', page, pageSize, search] as const,
  detail: (tenantId: string) => [...licenseKeys.all, 'detail', tenantId] as const,
};

export function useLicenses(page: number, pageSize: number, search = '') {
  return useQuery({
    queryKey: licenseKeys.list(page, pageSize, search),
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<LicenseListItem>>('/licenses', {
        params: { page, pageSize, search: search || undefined },
      });
      return data;
    },
  });
}

export function useLicense(tenantId: string) {
  return useQuery({
    queryKey: licenseKeys.detail(tenantId),
    queryFn: async () => {
      const { data } = await apiClient.get<LicenseListItem>(`/licenses/${tenantId}`);
      return data;
    },
    enabled: Boolean(tenantId),
  });
}

export interface LicenseFormInput {
  endpointLimit: number;
  expiryDate: string;
  /** Only meaningful on update — issuing a license always starts at 0. */
  endpointsUsed?: number;
}

/** Issues a license for a tenant chosen in the form itself (the License page's
 * "Issue license" flow isn't tied to a single tenant ahead of time). */
export function useCreateLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, ...dto }: LicenseFormInput & { tenantId: string }) => {
      const { data } = await apiClient.post<LicenseListItem>(`/tenants/${tenantId}/license`, dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: licenseKeys.all }),
    meta: { suppressErrorToast: true },
  });
}

export function useUpdateLicense(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: Partial<LicenseFormInput>) => {
      const { data } = await apiClient.patch<LicenseListItem>(`/tenants/${tenantId}/license`, dto);
      return data;
    },
    meta: { suppressErrorToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: licenseKeys.all }),
  });
}

/** Flips a license's enabled flag — used both from the License list page (target
 * tenant passed per call) and could be bound to a fixed tenantId if needed. */
export function useToggleLicenseEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, enabled }: { tenantId: string; enabled: boolean }) => {
      const { data } = await apiClient.patch(`/tenants/${tenantId}/license`, { enabled });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: licenseKeys.all }),
  });
}

export function useRegenerateLicenseKey(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<LicenseListItem>(`/tenants/${tenantId}/license/regenerate-key`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: licenseKeys.all }),
  });
}

export function useDeleteLicense(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/tenants/${tenantId}/license`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: licenseKeys.all }),
  });
}
